import { ListItemButton, ListItemIcon, ListSubheader } from '@mui/material';
// @mui
import { alpha, styled } from '@mui/material/styles';

//
import type { NavItemProps } from '../types';

// config
import { ICON, NAV } from '../../../config-global';

// ----------------------------------------------------------------------

type StyledItemProps = {
  caption?: boolean;
  disabled?: boolean;
} & Omit<NavItemProps, 'item'>;

export const StyledItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'caption',
})<StyledItemProps>(({ active, caption, depth, disabled, theme }) => {
  const isLight = theme.palette.mode === 'light';

  const subItem = depth !== 1;

  const activeStyle = {
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    color: theme.palette.primary.main,
    ...(!isLight && {
      color: theme.palette.primary.light,
    }),
  };

  const activeSubStyle = {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  };

  return {
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    height: NAV.H_DASHBOARD_ITEM,
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.5),
    position: 'relative',
    // Sub item
    ...(subItem && {
      height: NAV.H_DASHBOARD_ITEM_SUB,
      ...(depth > 2 && {
        paddingLeft: theme.spacing(depth),
      }),
      ...(caption && {
        height: NAV.H_DASHBOARD_ITEM,
      }),
    }),
    // Active item
    ...(active && {
      ...activeStyle,
      '&:hover': {
        ...activeStyle,
      },
    }),
    // Active sub item
    ...(subItem &&
      active && {
        ...activeSubStyle,
        '&:hover': {
          ...activeSubStyle,
        },
      }),
    // Disabled
    ...(disabled && {
      '&.Mui-disabled': {
        opacity: 0.64,
      },
    }),
  };
});

// ----------------------------------------------------------------------

export const StyledIcon = styled(ListItemIcon)({
  alignItems: 'center',
  display: 'flex',
  height: ICON.NAV_ITEM,
  justifyContent: 'center',
  width: ICON.NAV_ITEM,
});

// ----------------------------------------------------------------------

type StyledDotIconProps = {
  active?: boolean;
};

export const StyledDotIcon = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})<StyledDotIconProps>(({ active, theme }) => ({
  backgroundColor: theme.palette.text.disabled,
  borderRadius: '50%',
  height: 4,
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shorter,
  }),
  width: 4,
  ...(active && {
    backgroundColor: theme.palette.primary.main,
    transform: 'scale(2)',
  }),
}));

// ----------------------------------------------------------------------

export const StyledSubheader = styled(ListSubheader)(({ theme }) => ({
  ...theme.typography.overline,
  color: theme.palette.text.secondary,
  fontSize: 11,
  paddingBottom: theme.spacing(1),
  paddingTop: theme.spacing(3),
}));
